import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../config/firebase';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import ChessNav from './ChessNav';
import Icon from '../Shared/Icons';
import { getTier, DEFAULT_RATING } from '../../utils/chessRating';
import { C, FONT } from '../../utils/chessTheme';

const PLAYER_KEY = 'iyanna_chess_player';
const SNAP_KEY = 'iyanna_rank_snapshot';

function myId() {
  try { const raw = localStorage.getItem(PLAYER_KEY); return raw ? JSON.parse(raw).id : null; }
  catch { return null; }
}

// ── LIVE RANKING v2 — kitu chetu cha tofauti ───────────────────────────
// • Podium ya dhahabu... hapana — ya KIJANI kwa watatu bora.
// • Ngazi inasonga LIVE na kwa DRAMA: mchezaji akipanda, row yake inaruka
//   JUU ya wengine; akishuka inabonyea na kupita NYUMA yao.
// • Alama ▲/▼ zinaonyesha aliyepanda/kushuka tangu ulipotembelea mwisho.
// • Kadi yako binafsi juu — unajua nafasi yako papo hapo.
// • Gusa mchezaji yeyote — profile yake fupi inafunguka.

function loadSnapshot() {
  try { const raw = localStorage.getItem(SNAP_KEY); return raw ? JSON.parse(raw) : {}; }
  catch { return {}; }
}

export default function ChessRanking() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('wote'); // 'wote' | 'verified'
  const [popup, setPopup] = useState(null);     // player object
  const me = myId();

  const rowRefs = useRef(new Map());
  const prevRects = useRef(new Map());
  // Snapshot of positions from the PREVIOUS visit — for ▲/▼ arrows.
  const [lastVisit] = useState(loadSnapshot);
  const snapSaved = useRef(false);

  useEffect(() => {
    const q = query(collection(db, 'chessPlayers'), orderBy('rating', 'desc'), limit(60));
    const unsub = onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPlayers(list);
      setLoading(false);
      // Save this visit's positions ONCE (so arrows stay stable during the visit)
      if (!snapSaved.current && list.length) {
        snapSaved.current = true;
        const positions = {};
        list.forEach((p, i) => { positions[p.id] = i + 1; });
        try { localStorage.setItem(SNAP_KEY, JSON.stringify(positions)); } catch {}
      }
    }, () => setLoading(false));
    return unsub;
  }, []);

  const filtered = filter === 'verified' ? players.filter(p => p.verified) : players;
  const podium = filtered.slice(0, 3);
  const rest = filtered.slice(3);

  // FLIP: animate reorder of the list rows (below the podium)
  useLayoutEffect(() => {
    const newRects = new Map();
    rest.forEach((p, index) => {
      const el = rowRefs.current.get(p.id);
      if (!el) return;
      newRects.set(p.id, { top: el.getBoundingClientRect().top, index });
    });
    rest.forEach(p => {
      const el = rowRefs.current.get(p.id);
      const prev = prevRects.current.get(p.id);
      const next = newRects.get(p.id);
      if (!el || !prev || !next) return;
      const dy = prev.top - next.top;
      if (Math.abs(dy) < 2) return;
      const rising = next.index < prev.index;
      el.style.zIndex = rising ? 30 : 1;
      const anim = el.animate(
        rising
          ? [
              { transform: `translateY(${dy}px) scale(1)`, boxShadow: '0 0 0 rgba(0,0,0,0)' },
              { transform: `translateY(${dy * 0.5}px) scale(1.07)`, boxShadow: '0 18px 40px rgba(0,0,0,0.55)', offset: 0.45 },
              { transform: 'translateY(0) scale(1)', boxShadow: '0 0 0 rgba(0,0,0,0)' },
            ]
          : [
              { transform: `translateY(${dy}px) scale(1)`, opacity: 1 },
              { transform: `translateY(${dy * 0.5}px) scale(0.9)`, opacity: 0.55, offset: 0.5 },
              { transform: 'translateY(0) scale(1)', opacity: 1 },
            ],
        { duration: rising ? 750 : 850, easing: 'cubic-bezier(0.3, 0.8, 0.3, 1)' }
      );
      anim.onfinish = () => { el.style.zIndex = ''; };
    });
    prevRects.current = newRects;
  }, [rest]);

  function movement(p, position) {
    const prev = lastVisit[p.id];
    if (!prev) return null;
    const delta = prev - position; // positive = climbed
    if (delta === 0) return null;
    return delta;
  }

  const myIndex = filtered.findIndex(p => p.id === me);
  const myEntry = myIndex >= 0 ? filtered[myIndex] : null;
  const myMove = myEntry ? movement(myEntry, myIndex + 1) : null;

  function MoveTag({ delta }) {
    if (delta == null) return null;
    const up = delta > 0;
    return (
      <span style={{ ...styles.moveTag, color: up ? '#4ADE80' : '#FCA5A5' }}>
        {up ? '▲' : '▼'}{Math.abs(delta)}
      </span>
    );
  }

  function winRate(p) {
    const g = p.gamesPlayed || 0;
    if (!g) return 0;
    return Math.round(((p.wins || 0) / g) * 100);
  }

  return (
    <div style={styles.page}>
      <div style={styles.bar}>
        <button style={styles.backIconBtn} onClick={() => navigate('/chess')}>
          <Icon name="door" size={19} color={C.text} strokeWidth={2.4} />
        </button>
        <div style={styles.barTitle}>Ranking</div>
        <div style={{ width: '38px' }} />
      </div>

      {/* FILTERS */}
      <div style={styles.filterRow}>
        {[
          { id: 'wote', label: 'Wote' },
          { id: 'verified', label: 'Waliothibitishwa' },
        ].map(f => (
          <button
            key={f.id}
            style={{ ...styles.filterChip, ...(filter === f.id ? styles.filterChipActive : {}) }}
            onClick={() => setFilter(f.id)}
          >
            {f.id === 'verified' && <Icon name="verified" size={13} color={filter === f.id ? '#06281A' : C.accent} />}
            {f.label}
          </button>
        ))}
      </div>

      {/* MY POSITION CARD */}
      {myEntry && (
        <button style={styles.meCard} onClick={() => setPopup(myEntry)}>
          <div style={styles.meRank}>#{myIndex + 1}</div>
          <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <div style={styles.meName}>
              {myEntry.name} {myEntry.verified && <Icon name="verified" size={13} color={C.accent} />}
            </div>
            <div style={{ ...styles.meTier, color: getTier(myEntry.rating || 0).color }}>
              {getTier(myEntry.rating || 0).swahili}
            </div>
          </div>
          <MoveTag delta={myMove} />
          <div style={styles.meRating}>{myEntry.rating ?? 0}</div>
        </button>
      )}

      {loading ? (
        <div style={styles.empty}>Inapakia...</div>
      ) : filtered.length === 0 ? (
        <div style={styles.empty}>
          {filter === 'verified' ? 'Bado hakuna waliothibitishwa — jisajili kwenye Profile!' : 'Bado hakuna wachezaji — kuwa wa kwanza!'}
        </div>
      ) : (
        <>
          {/* PODIUM — watatu bora */}
          {podium.length > 0 && (
            <div style={styles.podium}>
              {[1, 0, 2].map(slot => {
                const p = podium[slot];
                if (!p) return <div key={slot} style={{ flex: 1 }} />;
                const position = slot + 1;
                const champion = slot === 0;
                const tier = getTier(p.rating || 0);
                return (
                  <button
                    key={p.id}
                    style={{ ...styles.podiumCol, ...(champion ? styles.podiumColFirst : {}) }}
                    onClick={() => setPopup(p)}
                  >
                    {champion && (
                      <div style={styles.crownFloat}>
                        <Icon name="crown" size={20} color="#6EE7A8" strokeWidth={2.2} />
                      </div>
                    )}
                    <div style={{ ...styles.podiumAvatar, ...(champion ? styles.podiumAvatarFirst : {}) }}>
                      {(p.name || '?')[0].toUpperCase()}
                    </div>
                    <div style={styles.podiumName}>
                      {p.name} {p.verified && <Icon name="verified" size={12} color={C.accent} />}
                    </div>
                    <div style={{ ...styles.podiumTier, color: tier.color }}>{tier.swahili.split(' ')[0]}</div>
                    <div style={styles.podiumRating}>{p.rating ?? 0}</div>
                    <MoveTag delta={movement(p, position)} />
                    <div style={{ ...styles.podiumBase, ...(champion ? styles.podiumBaseFirst : {}) }}>
                      {position}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* THE MOVING LADDER — nafasi 4 kwenda chini */}
          <div style={styles.list}>
            {rest.map((p, i) => {
              const position = i + 4;
              const tier = getTier(p.rating || DEFAULT_RATING);
              const isMe = p.id === me;
              return (
                <div
                  key={p.id}
                  ref={el => { if (el) rowRefs.current.set(p.id, el); else rowRefs.current.delete(p.id); }}
                  style={{ ...styles.row, ...(isMe ? styles.rowMe : {}) }}
                  onClick={() => setPopup(p)}
                >
                  <div style={styles.rank}>{position}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.name}>
                      <span style={styles.nameText}>{p.name}</span>
                      {p.verified && <Icon name="verified" size={14} color={C.accent} />}
                      {(p.meetGames || 0) >= 5 && (
                        <span style={styles.clubBadge} title="Anahudhuria mikutano sana">
                          <Icon name="parents" size={11} color="#06281A" /> {p.meetGames}
                        </span>
                      )}
                      {isMe && <span style={styles.meTag}>wewe</span>}
                    </div>
                    <div style={{ ...styles.tier, color: tier.color }}>{tier.swahili}</div>
                  </div>
                  <MoveTag delta={movement(p, position)} />
                  <div style={styles.ratingBox}>
                    <div style={styles.rating}>{p.rating ?? DEFAULT_RATING}</div>
                    <div style={styles.ratingLabel}>rating</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* PLAYER POPUP — profile fupi */}
      {popup && (
        <div style={styles.popOverlay} onClick={() => setPopup(null)}>
          <div style={styles.popCard} onClick={e => e.stopPropagation()}>
            <div style={styles.popAvatar}>{(popup.name || '?')[0].toUpperCase()}</div>
            <div style={styles.popName}>
              {popup.name} {popup.verified && <Icon name="verified" size={15} color={C.accent} />}
            </div>
            <div style={{ ...styles.popTier, color: getTier(popup.rating || 0).color }}>
              {getTier(popup.rating || 0).swahili}
            </div>
            <div style={styles.popRating}>{popup.rating ?? 0}</div>

            <div style={styles.popStats}>
              <div style={styles.popStat}>
                <div style={styles.popStatNum}>{popup.gamesPlayed || 0}</div>
                <div style={styles.popStatLbl}>Michezo</div>
              </div>
              <div style={styles.popStat}>
                <div style={{ ...styles.popStatNum, color: '#4ADE80' }}>{popup.wins || 0}</div>
                <div style={styles.popStatLbl}>Ushindi</div>
              </div>
              <div style={styles.popStat}>
                <div style={{ ...styles.popStatNum, color: '#FCA5A5' }}>{popup.losses || 0}</div>
                <div style={styles.popStatLbl}>Kushindwa</div>
              </div>
              <div style={styles.popStat}>
                <div style={{ ...styles.popStatNum, color: '#FBBF24' }}>{popup.coins || 0}</div>
                <div style={styles.popStatLbl}>Coins</div>
              </div>
            </div>

            {(popup.meetGames || 0) > 0 && (
              <div style={styles.meetLine}>
                <Icon name="parents" size={14} color={C.accent} />
                Amecheza michezo {popup.meetGames} ya mikutano (mashindano/klabu)
              </div>
            )}

            {/* Winrate bar */}
            <div style={styles.rateBarWrap}>
              <div style={{ ...styles.rateBar, width: `${winRate(popup)}%` }} />
            </div>
            <div style={styles.rateText}>Anashinda {winRate(popup)}% ya michezo yake</div>

            <button style={styles.popClose} onClick={() => setPopup(null)}>Funga</button>
          </div>
        </div>
      )}

      <ChessNav />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: C.bgGradient, padding: '20px 18px 130px', maxWidth: '620px', margin: '0 auto' },
  bar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' },
  backIconBtn: { width: '38px', height: '38px', borderRadius: '11px', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.line}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  barTitle: { color: C.text, fontFamily: FONT.display, fontSize: '19px', fontWeight: '800' },

  filterRow: { margin: '0 0 12px', display: 'flex', gap: '7px', justifyContent: 'center' },
  filterChip: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.line}`, color: C.textDim, fontSize: '12px', fontWeight: '800', padding: '9px 16px', borderRadius: '99px', cursor: 'pointer', fontFamily: 'inherit' },
  filterChipActive: { background: 'linear-gradient(135deg, #6EE7A8, #34D399)', border: 'none', color: '#06281A' },

  meCard: {
    margin: '0 0 16px', width: '100%',
    display: 'flex', alignItems: 'center', gap: '12px',
    background: 'linear-gradient(150deg, rgba(52,211,153,0.14), rgba(52,211,153,0.03))',
    border: '1.5px solid rgba(52,211,153,0.5)', borderRadius: '15px',
    padding: '12px 15px', cursor: 'pointer', fontFamily: 'inherit',
  },
  meRank: { color: '#06281A', background: 'linear-gradient(135deg, #6EE7A8, #34D399)', fontSize: '13px', fontWeight: '800', padding: '7px 11px', borderRadius: '11px', flexShrink: 0 },
  meName: { color: C.text, fontSize: '14px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px' },
  meTier: { fontSize: '10.5px', fontWeight: '700', marginTop: '2px' },
  meRating: { color: C.text, fontFamily: FONT.display, fontSize: '19px', fontWeight: '800', flexShrink: 0 },

  podium: { margin: '6px 0 18px', display: 'flex', alignItems: 'flex-end', gap: '8px' },
  podiumCol: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
    background: 'linear-gradient(180deg, #262D2A, #1E2421)', border: `1px solid ${C.line}`,
    borderRadius: '18px 18px 14px 14px', padding: '14px 8px 0', cursor: 'pointer',
    fontFamily: 'inherit', position: 'relative',
  },
  podiumColFirst: {
    border: '1.5px solid rgba(52,211,153,0.55)',
    background: 'linear-gradient(180deg, rgba(52,211,153,0.14), #1E2421)',
    boxShadow: '0 14px 34px rgba(52,211,153,0.14)',
    paddingTop: '22px',
  },
  crownFloat: { position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)' },
  podiumAvatar: {
    width: '46px', height: '46px', borderRadius: '50%',
    background: 'rgba(255,255,255,0.07)', border: '1.5px solid rgba(255,255,255,0.2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: C.text, fontFamily: FONT.display, fontSize: '19px', fontWeight: '800',
  },
  podiumAvatarFirst: { width: '56px', height: '56px', border: '2px solid rgba(52,211,153,0.6)', fontSize: '23px', color: '#D9F5E7' },
  podiumName: { color: C.text, fontSize: '12px', fontWeight: '800', maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' },
  podiumTier: { fontSize: '9.5px', fontWeight: '700' },
  podiumRating: { color: C.text, fontFamily: FONT.display, fontSize: '17px', fontWeight: '800' },
  podiumBase: {
    marginTop: '7px', width: '100%', textAlign: 'center',
    background: 'rgba(255,255,255,0.05)', borderTop: `1px solid ${C.line}`,
    borderRadius: '0 0 13px 13px', padding: '7px 0 9px',
    color: C.textDim, fontSize: '13px', fontWeight: '800',
  },
  podiumBaseFirst: { background: 'linear-gradient(135deg, #6EE7A8, #34D399)', color: '#06281A', padding: '10px 0 12px' },

  list: { display: 'flex', flexDirection: 'column', gap: '8px', position: 'relative' },
  row: {
    position: 'relative', display: 'flex', alignItems: 'center', gap: '12px',
    background: 'linear-gradient(150deg, #262D2A, #1F2522)',
    border: `1px solid ${C.line}`, borderRadius: '16px', padding: '12px 14px',
    willChange: 'transform', cursor: 'pointer',
  },
  rowMe: { border: '1.5px solid rgba(52,211,153,0.55)', background: 'linear-gradient(150deg, rgba(52,211,153,0.12), #1F2522)' },
  rank: {
    width: '34px', height: '34px', borderRadius: '11px', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.06)', color: C.textDim,
    fontSize: '13px', fontWeight: '800',
  },
  name: { display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' },
  nameText: { color: C.text, fontSize: '14px', fontWeight: '800', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' },
  meTag: { color: C.accent, fontSize: '10.5px', fontWeight: '800' },
  clubBadge: { display: 'inline-flex', alignItems: 'center', gap: '3px', background: 'linear-gradient(135deg, #6EE7A8, #34D399)', color: '#06281A', fontSize: '9.5px', fontWeight: '800', padding: '2px 7px', borderRadius: '99px' },
  meetLine: { display: 'flex', alignItems: 'center', gap: '7px', justifyContent: 'center', color: C.textDim, fontSize: '11.5px', fontWeight: '700', marginTop: '12px', background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: '11px', padding: '10px 12px' },
  tier: { fontSize: '10.5px', fontWeight: '700', marginTop: '3px' },
  moveTag: { fontSize: '11px', fontWeight: '800', flexShrink: 0 },
  ratingBox: { textAlign: 'right', flexShrink: 0 },
  rating: { color: C.text, fontFamily: FONT.display, fontSize: '18px', fontWeight: '800' },
  ratingLabel: { color: C.textFaint, fontSize: '9px', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase' },
  empty: { textAlign: 'center', color: C.textDim, padding: '40px 0' },

  popOverlay: { position: 'fixed', inset: 0, zIndex: 950, background: 'rgba(10,13,12,0.82)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '22px' },
  popCard: { width: '100%', maxWidth: '310px', textAlign: 'center', background: 'linear-gradient(180deg, #2A302D, #1E2421)', border: `1px solid ${C.lineStrong}`, borderRadius: '22px', padding: '24px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', boxShadow: '0 26px 70px rgba(0,0,0,0.6)' },
  popAvatar: { width: '62px', height: '62px', borderRadius: '50%', background: 'rgba(52,211,153,0.1)', border: '2px solid rgba(52,211,153,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#D9F5E7', fontFamily: FONT.display, fontSize: '26px', fontWeight: '800', marginBottom: '8px' },
  popName: { color: C.text, fontFamily: FONT.display, fontSize: '19px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px' },
  popTier: { fontSize: '11.5px', fontWeight: '800' },
  popRating: { color: C.text, fontFamily: FONT.display, fontSize: '28px', fontWeight: '800', marginTop: '4px' },
  popStats: { display: 'flex', gap: '6px', width: '100%', marginTop: '14px' },
  popStat: { flex: 1, background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.line}`, borderRadius: '12px', padding: '10px 4px' },
  popStatNum: { color: C.text, fontSize: '15.5px', fontWeight: '800' },
  popStatLbl: { color: C.textFaint, fontSize: '9px', fontWeight: '800', letterSpacing: '0.4px', textTransform: 'uppercase', marginTop: '2px' },
  rateBarWrap: { width: '100%', height: '7px', borderRadius: '99px', background: 'rgba(255,255,255,0.08)', marginTop: '14px', overflow: 'hidden' },
  rateBar: { height: '100%', borderRadius: '99px', background: 'linear-gradient(90deg, #0E9F6E, #6EE7A8)' },
  rateText: { color: C.textDim, fontSize: '11px', fontWeight: '700', marginTop: '6px' },
  popClose: { marginTop: '14px', background: 'linear-gradient(135deg, #0E9F6E, #34D399)', color: '#06281A', border: 'none', padding: '11px 26px', borderRadius: '11px', fontSize: '13px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
};
